import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { bankService } from './clients/ApiClients';
import { StopPaymentReturn } from './clients/rust-api-client';
import { getBankErrorParts } from './utils/bankHelpers';
import { describeKey } from './utils/stopPaymentHelpers';

interface ReleaseStopPaymentDialogProps {
  /** The stop being released; `null` closes the dialog. */
  stop: StopPaymentReturn | null;
  hoaId: string;
  onClose: () => void;
  onReleased: (after: StopPaymentReturn) => void;
}

/**
 * Release a stop payment. An active stop is deleted at the bank — the
 * check will pay if presented afterwards. A failed stop the bank never
 * held is simply removed from the list.
 */
const ReleaseStopPaymentDialog: React.FC<ReleaseStopPaymentDialogProps> = ({
  stop,
  hoaId,
  onClose,
  onReleased,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stop) setError(null);
  }, [stop]);

  const atBank = stop?.status === 'active';

  const handleRelease = async () => {
    if (!stop) return;
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.releaseHoaStopPayment({
        hoaId,
        id: stop.id,
      });
      onReleased(resp.data);
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setError(body || "Couldn't release the stop.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!stop} onClose={submitting ? () => undefined : onClose}>
      <DialogTitle>
        {atBank ? 'Release stop payment' : 'Remove stop payment'}
      </DialogTitle>
      <DialogDescription>
        {stop ? describeKey(stop) : ''}
        {stop?.reason ? ` — ${stop.reason}` : ''}
      </DialogDescription>
      <DialogBody>
        {error && (
          <ErrorAlert className="mb-4" title="Cannot release" onDismiss={() => setError(null)}>
            {error}
          </ErrorAlert>
        )}
        <Text>
          {atBank
            ? 'The bank will no longer refuse this check. It will pay if it is presented after this.'
            : 'The bank never accepted this stop; this removes it from the list.'}
        </Text>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="red" onClick={handleRelease} disabled={submitting}>
          {submitting ? 'Releasing…' : atBank ? 'Release stop' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseStopPaymentDialog;
